import { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  useBuildPtoPprSchemesMutation,
  useDeletePtoPprFigureMutation,
  useGetPtoPprFigureImageQuery,
  useUpdatePtoPprFigureMutation,
  useUploadPtoPprFigureMutation,
  type PtoPprFigure,
} from '@shared/api';
import { ACCEPT_IMAGE, ACCEPT_PDF } from '@shared/lib/fileAccept';
import { Button } from '@shared/ui/Button';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { Banner, errorText } from '../kfo/ui';
import { Small } from './ui';

/** Итог какого расчёта показать после перестройки — по разделу. */
const CALC_OF_SECTION: Record<string, string> = {
  app_labor: 'labor',
  app_schedule: 'schedule',
  app_sgp: 'site_plan',
};

/** Что строит программа для раздела. */
const BUILD_HINT: Record<string, string> = {
  scheme: 'по плану свай из векторного листа РД: захватки по копрам, порядок, опасные зоны',
  app_rigging: 'по марке и массе сваи из РД: строповка, подъём на копёр, складирование',
  app_labor: 'по нормам ГЭСН из базы Сметчика: сваи из РД, копёр из перечня техники',
  app_schedule: 'по машинному времени копров из калькуляции, захватки — как на схеме',
  app_sgp: 'по плану свай из РД: опасная зона копров, склады свай, дорога, стоянки крана',
};

/** Чертёж из AutoCAD (PDF), скриншот или снимок схемы. */
const ACCEPT_FIGURE = `${ACCEPT_PDF},${ACCEPT_IMAGE}`;

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
`;

const Card = styled.figure`
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
`;

const Thumb = styled.button`
  display: block;
  width: 100%;
  aspect-ratio: 4 / 3;
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  overflow: hidden;
  cursor: zoom-in;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
    background: #fff;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Placeholder = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const CaptionInput = styled.input`
  width: 100%;
  font-size: 13px;
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CardFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
`;

/**
 * Рисунки раздела (графическая часть ППР/ТК, §8.1): превью, подпись,
 * удаление и загрузка своих чертежей. PDF — по рисунку на лист; широкий
 * рисунок печатается на альбомном листе. У раздела «за инженером» рисунки в
 * DOCX стоят вместо заглушки.
 */
export function FiguresBlock({
  draftId,
  sectionKey,
  figures,
  canWrite,
  engineer,
  scheme = false,
}: {
  draftId: string;
  sectionKey: string;
  figures: PtoPprFigure[];
  canWrite: boolean;
  /** Раздел «за инженером»: без рисунков в DOCX остаётся заглушка. */
  engineer: boolean;
  /** Раздел схемы производства работ: программа строит её по плану свай из РД. */
  scheme?: boolean;
}) {
  const [upload, { isLoading: uploading }] = useUploadPtoPprFigureMutation();
  const [buildSchemes, { isLoading: building }] = useBuildPtoPprSchemesMutation();
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  async function onBuild() {
    setError('');
    setNotice('');
    try {
      const res = await buildSchemes(draftId).unwrap();
      const kind = CALC_OF_SECTION[sectionKey] ?? 'work_scheme';
      const calc = res.draft.facts?.calcs?.find((c) => c.kind === kind);
      if (calc) setNotice(calc.text);
    } catch (e) {
      setError(errorText(e, 'Не удалось построить схемы по РД.'));
    }
  }

  async function onFiles(files: File[]) {
    setError('');
    for (const file of files) {
      const form = new FormData();
      form.append('file', file, file.name);
      try {
        await upload({ draftId, key: sectionKey, form }).unwrap();
      } catch (e) {
        setError(errorText(e, `Не удалось приложить «${file.name}».`));
        return;
      }
    }
  }

  if (figures.length === 0 && !canWrite) return null;
  return (
    <Wrap data-figures={sectionKey}>
      {figures.length > 0 && (
        <Grid>
          {figures.map((f) => (
            <FigureCard key={f.id} figure={f} canWrite={canWrite} />
          ))}
        </Grid>
      )}
      {notice && <Banner $tone="info">{notice}</Banner>}
      {error && <Banner $tone="danger">{error}</Banner>}
      {canWrite && scheme && (
        <div>
          <Button $variant="ghost" $size="sm" type="button" disabled={building} onClick={onBuild}>
            {building
              ? 'Строю схемы…'
              : figures.some((f) => f.origin === 'generated')
                ? 'Перестроить схемы по РД'
                : 'Построить схемы по РД'}
          </Button>{' '}
          <Small>{BUILD_HINT[sectionKey] ?? BUILD_HINT.scheme}</Small>
        </div>
      )}
      {canWrite && (
        <FileDropZone
          multiple
          compact
          accept={ACCEPT_FIGURE}
          disabled={uploading}
          primary={
            uploading
              ? 'Загрузка рисунка…'
              : engineer && figures.length === 0
                ? 'Приложить чертёж или схему (PDF, PNG, JPG)'
                : 'Добавить рисунок (PDF, PNG, JPG)'
          }
          hint="PDF — по рисунку на лист; широкий рисунок печатается на альбомном листе"
          onFiles={onFiles}
        />
      )}
    </Wrap>
  );
}

function FigureCard({ figure, canWrite }: { figure: PtoPprFigure; canWrite: boolean }) {
  const { data: blob, isError } = useGetPtoPprFigureImageQuery({
    draftId: figure.draft_id,
    id: figure.id,
  });
  const [url, setUrl] = useState<string | null>(null);
  const [caption, setCaption] = useState(figure.caption);
  const [error, setError] = useState('');
  const [update] = useUpdatePtoPprFigureMutation();
  const [remove, { isLoading: removing }] = useDeletePtoPprFigureMutation();

  useEffect(() => {
    if (!blob) return;
    const objectUrl = URL.createObjectURL(blob);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);

  useEffect(() => setCaption(figure.caption), [figure.caption]);

  async function saveCaption() {
    const next = caption.trim();
    if (next === figure.caption) return;
    setError('');
    try {
      await update({ draftId: figure.draft_id, id: figure.id, caption: next }).unwrap();
    } catch (e) {
      setError(errorText(e, 'Не удалось сохранить подпись.'));
    }
  }

  async function onDelete() {
    if (!window.confirm(`Удалить рисунок «${figure.caption || 'без подписи'}»?`)) return;
    setError('');
    try {
      await remove({ draftId: figure.draft_id, id: figure.id }).unwrap();
    } catch (e) {
      setError(errorText(e, 'Не удалось удалить рисунок.'));
    }
  }

  const origin = figure.origin === 'generated' ? 'построено программой' : figure.source_name;
  return (
    <Card>
      <Thumb
        type="button"
        title="Открыть в полном размере"
        aria-label={`Рисунок: ${figure.caption}`}
        onClick={() => {
          if (url) window.open(url, '_blank', 'noopener');
        }}
      >
        {url ? (
          <img src={url} alt={figure.caption} loading="lazy" />
        ) : (
          <Placeholder>{isError ? 'не загрузилось' : 'загрузка…'}</Placeholder>
        )}
      </Thumb>
      {canWrite ? (
        <CaptionInput
          value={caption}
          aria-label="Подпись рисунка"
          placeholder="Подпись рисунка"
          onChange={(e) => setCaption(e.target.value)}
          onBlur={saveCaption}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
        />
      ) : (
        <figcaption>{figure.caption}</figcaption>
      )}
      <CardFoot>
        <Small>
          {origin}
          {figure.landscape ? ' · альбомный лист' : ''}
        </Small>
        {canWrite && (
          <Button $variant="ghost" $size="sm" type="button" disabled={removing} onClick={onDelete}>
            Удалить
          </Button>
        )}
      </CardFoot>
      {error && <Banner $tone="danger">{error}</Banner>}
    </Card>
  );
}
